
import Vue from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import { mapActions, mapGetters } from 'vuex'
import { stripeTestApiKey, stripeLiveApiKey } from '../../config'
import { RInput, RCard } from '@/components/'
import { CardSummary, StorageTierCodename, BundlePeriod } from '@/api/types/user'
import { profiliatePostCustomer } from '@/services/proafiliate'

/**
 * This is a modified PaymentDetails.vue
 *
 * The aim is to reduce coupling with onboarding
 */

type CardDetailsFormData = {
  editingCard: boolean
  stripe: any
  cardholderName: ''
  cardNumberElement: any
  cardExpiryElement: any
  cardCvcElement: any

  cardNumberComplete: boolean
  cardExpiryComplete: boolean
  cardCvcComplete: boolean

  cardNumberError: string
  cardExpiryError: string
  cardCvcError: string
  windowWidth: number

  affiliateHash: string
}

export default Vue.extend({
  name: 'CardDetailsForm',
  components: {
    RCard,
    RInput,
  },

  props: {
    allowTrial: {
      type: Boolean,
      default: false,
    },
  },

  head: {
    script: [
      {
        src: 'https://renderro.postaffiliatepro.com/scripts/vjynyel',
        type: 'text/javascript',
        id: 'pap_x2s6df8d',
      },
    ],
  },

  data(): CardDetailsFormData {
    return {
      editingCard: false,
      // Default payment method:
      stripe: null,
      cardholderName: '',
      cardNumberElement: null,
      cardExpiryElement: null,
      cardCvcElement: null,

      cardNumberComplete: false,
      cardExpiryComplete: false,
      cardCvcComplete: false,

      cardNumberError: '',
      cardExpiryError: '',
      cardCvcError: '',
      windowWidth: window.innerWidth,
      //proAffiliate
      affiliateHash: '',
    }
  },

  created() {
    window.addEventListener('resize', () => (this.windowWidth = window.innerWidth))
    this.resetPaymentState()
  },

  async mounted() {
    const stripeApiKey =
      this.user.profile.environment === 'dev' ? stripeTestApiKey : stripeLiveApiKey
    this.stripe = await loadStripe(stripeApiKey)
    const elements = this.stripe.elements()

    const elementStyles = {
      base: {
        color: '#E2E2E3',
        fontSize: this.isDesktop ? '18px' : '16px',
        fontFamily: 'Open Sans, sans-serif',
        lineHeight: '44px',
        '::placeholder': {
          color: 'rgba(255,255,255,0.6)',
        },
        ':focus': {
          color: '#fff',
        },
      },
      invalid: {
        color: '#FC3333',
        iconColor: '#fa755a',
      },
    }

    const elementClasses = {
      focus: 'focused',
      empty: 'empty',
      invalid: 'invalid',
    }

    const options = {
      style: elementStyles,
      classes: elementClasses,
    }
    this.cardNumberElement = elements.create('cardNumber', options)
    this.cardNumberElement.mount('#card-number-element')
    this.cardExpiryElement = elements.create('cardExpiry', options)
    this.cardExpiryElement.mount('#card-expiry-element')
    this.cardCvcElement = elements.create('cardCvc', options)
    this.cardCvcElement.mount('#card-cvc-element')

    this.cardNumberElement.on('change', ({ error, complete }: any) => {
      if (error) {
        this.cardNumberError = error.message
      } else {
        this.cardNumberError = ''
      }
      this.cardNumberComplete = complete
      this.validateCardInput()
    })

    this.cardExpiryElement.on('change', ({ error, complete }: any) => {
      if (error) {
        this.cardExpiryError = error.message
      } else {
        this.cardExpiryError = ''
      }
      this.cardExpiryComplete = complete
      this.validateCardInput()
    })

    this.cardCvcElement.on('change', ({ error, complete }: any) => {
      if (error) {
        this.cardCvcError = error.message
      } else {
        this.cardCvcError = ''
      }
      this.cardCvcComplete = complete
      this.validateCardInput()
    })
    this.$root.$on('save-card', this.saveCard)

    // ProAffiliate
    profiliatePostCustomer() // sets cookie for proaffiliate to hidden input
    this.affiliateHash = (this.$refs.proAffiliateInput as HTMLInputElement).value
  },
  beforeDestroy() {
    this.$root.$off('save-card', this.saveCard)
  },
  methods: {
    ...mapActions({
      setupWithPayment: 'user/setup',
      resetPaymentState: 'payment/resetPaymentState',
    }),
    async saveCard({
      codename,
      bundlePeriod,
    }: {
      codename: StorageTierCodename
      bundlePeriod: BundlePeriod
    }): Promise<void> {
      const { cardholderName, cardNumberElement: card, stripe } = this
      const paymentMethod = {
        stripe,
        cardholderName,
        card,
      }

      await this.setupWithPayment({
        codename,
        paymentMethod,
        bundlePeriod,
        allowTrial: true,
        affiliateHash: this.affiliateHash,
      })
      this.cardNumberElement.clear()
      this.cardExpiryElement.clear()
      this.cardCvcElement.clear()
      this.cardholderName = ''
      this.editingCard = false

      if (this.setupRequest.successful) {
        this.$store.commit('payment/setIsPaymentComplete', true)
      }
    },
    validateCardInput(): void {
      this.$store.commit('payment/setIsPaymentValid', !this.saveCardDisabled)
    },
  },

  computed: {
    ...mapGetters({
      billing: 'user/billing',
      billingUpdateRequest: 'user/billingUpdateRequest',
      setupRequest: 'user/setupRequest',
      user: 'user/user',
      theme: 'theme',
    }),
    saveCardDisabled(): boolean {
      return (
        this.setupRequest.pending ||
        !this.cardholderName ||
        !this.cardNumberComplete ||
        !this.cardExpiryComplete ||
        !this.cardCvcComplete
      )
    },
    cardSummary(): CardSummary | null {
      const card = this.billing && this.billing.card ? this.billing.card : null
      if (!card) {
        return null
      }
      return card
    },
    labelBackground(): string {
      if (this.isDesktop) {
        return 'transparent'
      } else return 'var(--bgTertiary)'
    },
    isDesktop(): boolean {
      return this.windowWidth > 992
    },
  },
})
